import {useEffect, useMemo} from 'react';
import {create} from 'zustand';

import {
  LegacyRegistryProjectsQuery,
  RegistryProjectsWithTypeQuery,
  useLegacyRegistryProjectsQuery,
  useRegistryProjectsWithTypeQuery,
} from '../../../../../generated/graphql';
import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../../../util/artifacts';
import {propagateErrorsContext} from '../../../../../util/errors';
import {capitalizeFirstLetter} from '../../../../../util/string';
import {checkRegistryProject} from '../../../common/utils';
import {RegistryProject} from '../../CreateRegistryDrawer/CreateRegistryDrawerInner';
import {Artifact, Collection} from '../CreateRegisteredCollectionDrawerInner';
import {GenericOptionType} from './DrawerPageCollectionConfiguration';

type RegistryDrawerStoreState = {
  registryOptions: GenericOptionType[];
  selectedRegistry: GenericOptionType | undefined;
  existingRegistryNames: Set<string>;
  orgEntityName: string;
  orgId: string | undefined;
  registryOptionsLoading: boolean;
  isRefetchingAfterCreate: boolean;
  pendingRegistryValue: string | undefined;
  collection: Collection;
  // Initialization props needed to derive collection state
  initProps:
    | {
        artifactTypeName: string;
        defaultProjectName: string;
        artifact: Artifact;
        enableGlobalRegistry: boolean;
      }
    | undefined;
};

const getInitialCollection = (
  artifactTypeName: string,
  defaultProjectName: string,
  artifact: Artifact,
  enableGlobalRegistry: boolean,
  orgEntityName: string
): Collection => ({
  name: '',
  id: '',
  description: '',
  entityName: enableGlobalRegistry
    ? orgEntityName
    : (artifact.entityName ?? ''),
  projectName: enableGlobalRegistry ? '' : defaultProjectName,
  tags: [],
  numVersions: 0,
  artifactTypeName,
});

const initialRegistryDrawerState: RegistryDrawerStoreState = {
  registryOptions: [],
  selectedRegistry: undefined,
  existingRegistryNames: new Set(),
  orgEntityName: '',
  orgId: undefined,
  registryOptionsLoading: false,
  isRefetchingAfterCreate: false,
  pendingRegistryValue: undefined,
  collection: {
    name: '',
    id: '',
    description: '',
    entityName: '',
    projectName: '',
    tags: [],
    numVersions: 0,
    artifactTypeName: '',
  },
  initProps: undefined,
};

export const useRegistryDrawerStore = create<RegistryDrawerStoreState>(() => ({
  ...initialRegistryDrawerState,
}));

const registrySet = useRegistryDrawerStore.setState;
const registryGet = useRegistryDrawerStore.getState;

/**
 * Module-level ref to store the Apollo refetch function.
 * React hook functions shouldn't be stored in Zustand state as they're tied to component lifecycle.
 */
let refetchRegistryOptionsRef: (() => Promise<unknown>) | undefined;

const buildExistingRegistryNameSet = (
  registryOptions: GenericOptionType[]
): Set<string> => {
  const names = new Set<string>();
  registryOptions.forEach(opt => {
    const {registryName} = checkRegistryProject(opt.value);
    if (registryName) {
      names.add(registryName.toLowerCase());
    }
  });
  return names;
};

const buildRegistryOptions = (
  orgRegistryProjects: RegistryProjectsWithTypeQuery | undefined,
  legacyRegistryProjects: LegacyRegistryProjectsQuery | undefined,
  artifactTypeName: string
): GenericOptionType[] => {
  const newRegistryOptions: GenericOptionType[] = [];

  for (const edge of orgRegistryProjects?.organization?.registries?.edges ??
    []) {
    const registryProject = edge?.node;
    if (registryProject != null) {
      const allowsArtifactType =
        registryProject.artifactType != null ||
        registryProject.allowAllArtifactTypesInRegistry;
      const {registryName} = checkRegistryProject(registryProject.name);
      if (registryName != null && allowsArtifactType) {
        newRegistryOptions.push({
          value: registryProject.name,
          label: capitalizeFirstLetter(registryName),
        });
      }
    }
  }

  const legacyModelProject =
    legacyRegistryProjects?.projects?.edges?.find(
      edge => edge?.node?.name === OLD_MODEL_REGISTRY_PROJECT_NAME
    )?.node ?? undefined;

  const legacyModelProjectArtifactCollectionsCount =
    legacyModelProject?.totalArtifactCollections ?? 0;

  if (
    artifactTypeName === 'model' &&
    // PM decision: we will not show the legacy model registry entry
    // if the user hasn't actually added any collections to it. We don't
    // want to encourage usage of the legacy model registry since we are
    // deprecating it.
    legacyModelProjectArtifactCollectionsCount > 0
  ) {
    newRegistryOptions.push({
      value: OLD_MODEL_REGISTRY_PROJECT_NAME,
      label: 'Legacy model registry',
    });
  }

  return newRegistryOptions;
};

export const RegistryDrawerStoreActions = {
  /**
   * Resets the store to initial state and clears the refetch ref.
   * Should be called when the drawer is closed/unmounted.
   */
  reset: () => {
    refetchRegistryOptionsRef = undefined;
    registrySet({...initialRegistryDrawerState});
  },
  /**
   * Initializes the store with drawer configuration.
   * Should be called when the drawer opens.
   */
  init: (
    artifactTypeName: string,
    defaultProjectName: string,
    artifact: Artifact,
    enableGlobalRegistry: boolean
  ) => {
    const state = registryGet();
    const initialCollection = getInitialCollection(
      artifactTypeName,
      defaultProjectName,
      artifact,
      enableGlobalRegistry,
      state.orgEntityName || (artifact.entityName ?? '')
    );
    registrySet({
      initProps: {
        artifactTypeName,
        defaultProjectName,
        artifact,
        enableGlobalRegistry,
      },
      collection: initialCollection,
    });
  },
  setRegistryOptionsLoading: (registryOptionsLoading: boolean) =>
    registrySet({registryOptionsLoading}),
  /**
   * Updates registry data from queries.
   * Also updates collection.entityName if global registry is enabled and orgEntityName changes.
   */
  setRegistryData: ({
    registryOptions,
    orgEntityName,
    orgId,
  }: {
    registryOptions: GenericOptionType[];
    orgEntityName: string;
    orgId: string | undefined;
  }) => {
    const state = registryGet();
    const shouldUpdateCollectionEntityName =
      state.initProps?.enableGlobalRegistry &&
      orgEntityName !== '' &&
      state.collection.entityName !== orgEntityName;

    registrySet({
      registryOptions,
      orgEntityName,
      orgId,
      existingRegistryNames: buildExistingRegistryNameSet(registryOptions),
      ...(shouldUpdateCollectionEntityName && {
        collection: {
          ...state.collection,
          entityName: orgEntityName,
        },
      }),
    });
    RegistryDrawerStoreActions.trySelectPendingRegistry();
  },
  /**
   * Sets the selected registry and automatically updates collection.entityName and collection.projectName.
   */
  setSelectedRegistry: (selectedRegistry: GenericOptionType | undefined) => {
    const state = registryGet();
    if (!selectedRegistry || !state.initProps) {
      registrySet({selectedRegistry});
      return;
    }

    const newProjectName = selectedRegistry.value;
    const derivedEntityName =
      state.initProps.enableGlobalRegistry &&
      newProjectName !== OLD_MODEL_REGISTRY_PROJECT_NAME
        ? state.orgEntityName
        : (state.initProps.artifact.entityName ?? '');

    const currentCollection = state.collection;
    // Skip update if values haven't changed
    if (
      currentCollection.projectName === newProjectName &&
      currentCollection.entityName === derivedEntityName
    ) {
      registrySet({selectedRegistry});
      return;
    }

    registrySet({
      selectedRegistry,
      collection: {
        ...currentCollection,
        entityName: derivedEntityName,
        projectName: newProjectName,
      },
    });
  },
  /**
   * Stores the Apollo refetch function in a module-level ref.
   * Called by useRegistryStoreOptions hook when refetch function is available.
   */
  setRefetchRegistryOptions: (
    refetchRegistryOptions: (() => Promise<unknown>) | undefined
  ) => {
    refetchRegistryOptionsRef = refetchRegistryOptions;
  },
  /**
   * Handles registry creation: sets pending state, refetches options, then selects the new registry.
   */
  handleRegistryCreated: async (registryInfo: RegistryProject) => {
    if (!refetchRegistryOptionsRef) {
      throw new Error('Refetch registry options ref not set');
    }
    registrySet({
      pendingRegistryValue: registryInfo.projectName,
      isRefetchingAfterCreate: true,
    });
    try {
      await refetchRegistryOptionsRef();
    } catch (err) {
      registrySet({
        pendingRegistryValue: undefined,
        isRefetchingAfterCreate: false,
      });
      throw err;
    }
  },
  /**
   * Attempts to select a registry that was just created.
   * Called after refetch completes to automatically select the newly created registry.
   */
  trySelectPendingRegistry: () => {
    const state = registryGet();
    if (!state.pendingRegistryValue) {
      if (state.isRefetchingAfterCreate) {
        registrySet({isRefetchingAfterCreate: false});
      }
      return;
    }
    const match = state.registryOptions.find(
      opt => opt.value === state.pendingRegistryValue
    );
    if (match) {
      // Use setSelectedRegistry to properly update both selectedRegistry and collection
      RegistryDrawerStoreActions.setSelectedRegistry(match);
      registrySet({
        pendingRegistryValue: undefined,
        isRefetchingAfterCreate: false,
      });
    }
  },
  /**
   * Sets the collection state. Accepts either a Collection object or an updater function.
   */
  setCollection: (
    collection: Collection | ((prev: Collection) => Collection)
  ) => {
    const state = registryGet();
    const newCollection =
      typeof collection === 'function'
        ? collection(state.collection)
        : collection;
    registrySet({collection: newCollection});
  },
  /**
   * Partially updates the collection state.
   */
  updateCollection: (updates: Partial<Collection>) => {
    const state = registryGet();
    registrySet({
      collection: {
        ...state.collection,
        ...updates,
      },
    });
  },
};

/**
 * Hook that manages registry options queries and syncs data to the store.
 * Handles both new global registry projects and legacy model registry.
 */
export const useRegistryStoreOptions = (
  artifactTypeName: string,
  orgName: string | undefined,
  enableGlobalRegistry: boolean,
  artifact: Artifact
) => {
  // Get the new global registry projects
  const {
    data: orgRegistryProjects,
    loading: orgRegistryProjectsLoading,
    refetch: refetchRegistryProjects,
  } = useRegistryProjectsWithTypeQuery({
    variables: {orgName: orgName ?? '', artifactTypeName},
    context: propagateErrorsContext(),
    skip: orgName == null || !enableGlobalRegistry,
  });

  // Get the legacy model registry project. Note that it may not exist.
  const {
    data: legacyModelRegistryProject,
    loading: legacyModelRegistryProjectLoading,
  } = useLegacyRegistryProjectsQuery({
    variables: {entityName: artifact.entityName ?? ''},
    context: propagateErrorsContext(),
    skip:
      // The only type of legacy registry we have is for models
      artifactTypeName !== 'model' ||
      // when global registry isn't turned on, we don't show a list of registries, so
      // no need for this query
      !enableGlobalRegistry,
  });

  useEffect(() => {
    RegistryDrawerStoreActions.setRegistryOptionsLoading(
      orgRegistryProjectsLoading || legacyModelRegistryProjectLoading
    );
  }, [orgRegistryProjectsLoading, legacyModelRegistryProjectLoading]);

  useEffect(() => {
    RegistryDrawerStoreActions.setRefetchRegistryOptions(
      refetchRegistryProjects
    );
  }, [refetchRegistryProjects]);

  const registryOptions = useMemo(
    () =>
      buildRegistryOptions(
        orgRegistryProjects,
        legacyModelRegistryProject,
        artifactTypeName
      ),
    [orgRegistryProjects, legacyModelRegistryProject, artifactTypeName]
  );

  const orgEntityName =
    orgRegistryProjects?.organization?.registries?.edges?.[0]?.node
      ?.entityName ?? artifact.entityName;

  const orgId = orgRegistryProjects?.organization?.id;

  useEffect(() => {
    // Always call setRegistryData when dependencies change, even if queries are skipped.
    // This ensures the store is updated with the correct initial state.
    RegistryDrawerStoreActions.setRegistryData({
      registryOptions,
      orgEntityName,
      orgId,
    });
  }, [registryOptions, orgEntityName, orgId]);
};
